import React from "react";

import { useMutation, useQuery } from "@apollo/client";
import { Box, Button, Divider, Link, Typography } from "@mui/material";
import Cookies from "js-cookie";
import { makeStyles, withStyles } from "tss-react/mui";

import CouponCodeDialog from "@/components/CouponCodeDialog";
import OjieWithRedFlagIcon from "@/components/Icons/OjieWithRedFlagIcon";
import { graphql } from "@/gql";
import { Package } from "@/gql/graphql";
import useBooleanToggle from "@/hooks/useBooleanToggle";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";
import ojieCry from "@assets/icons/ojie-cry@3x.png";
import nutricoachLogo from "@assets/Nutricoach.png";

const useStyles = makeStyles()({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: colors.white,
  },
  logo: {
    position: "absolute",
    top: 20,
    right: 20,
    width: 150,
    height: 50,
    alignSelf: "flex-end",
  },
  content: {
    width: 562,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 12,
  },
  message: {
    width: 482,
    marginBottom: 32,
    fontWeight: 500,
    textAlign: "center",
    color: colors.grey,
  },
  button: {
    marginBottom: 36,
    "& .MuiButton-label": {
      textTransform: "none",
    },
  },
  link: {
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: 700,
  },
  divider: {
    marginLeft: 16,
    marginRight: 16,
    backgroundColor: "#C8CED8",
  },
});

const reinstateAccountMutation = graphql(`
  mutation ReinstateAccount($input: CreateStripeCheckoutSessionInput!) {
    createStripeCheckoutSession(input: $input) {
      checkoutUrl
    }
  }
`);

const coachSubscriptionInfo = graphql(`
  query CoachSubscriptionInfo {
    coachViewer {
      id
      subscribedPackage
    }
  }
`);

const StyledOjieWithRedFlagIcon = withStyles(OjieWithRedFlagIcon, {
  root: {
    marginBottom: 26,
    width: 130,
    height: 130,
  },
});

const PaymentFailedPage: React.FC = () => {
  const { classes } = useStyles();
  const snackbar = useGlobalSnackbar();
  const { data } = useQuery(coachSubscriptionInfo);
  const subscriptionPackage = data?.coachViewer.subscribedPackage as Package;
  const [_reinstateAccount] = useMutation(reinstateAccountMutation);

  const reinstateAccount = () => {
    const successUrl = `${import.meta.env.VITE_APP_URL}/payment-successful`;
    const cancelUrl = `${import.meta.env.VITE_APP_URL}/subscription`;
    _reinstateAccount({
      variables: {
        input: {
          package: subscriptionPackage,
          successUrl: successUrl,
          cancelUrl: cancelUrl,
        },
      },
    })
      .then((result) => {
        const { checkoutUrl } = result.data?.createStripeCheckoutSession ?? {};
        if (checkoutUrl) {
          window.location.replace(checkoutUrl);
        }
      })
      .catch((error) => {
        snackbar.open(displayError(error));
      });
  };

  const logout = () => {
    Cookies.remove("token");
    window.location.replace("/login");
  };

  return (
    <div className={classes.root}>
      <img className={classes.logo} src={nutricoachLogo} />
      <StyledOjieWithRedFlagIcon />
      <Typography className={classes.title} variant="h4">
        Account Suspended
      </Typography>
      <Typography className={classes.message} variant="caption">
        Oh no! We were unable to process your payment. Please update your
        payment details to regain access to NutriCoach and continue working with
        your clients.
      </Typography>
      <Button className={classes.button} onClick={reinstateAccount}>
        Reactivate my account
      </Button>
      <Box display="flex">
        <Typography variant="caption">
          Got another account?{" "}
          <Link className={classes.link} component="button" onClick={logout}>
            Sign in here
          </Link>
        </Typography>
        <Divider className={classes.divider} orientation="vertical" />
        <Typography variant="caption">
          For concerns,{" "}
          <Link className={classes.link} href="https://support.nutricoach.com">
            contact us
          </Link>
          .
        </Typography>
      </Box>
    </div>
  );
};

export default PaymentFailedPage;
